/**
 * The parts of the market view workspace that all three of its pages share.
 *
 * The workspace does three jobs — create a market view from scratch ({@link ./NewMarketView}), create
 * one by copying an existing one ({@link ./CloneMarketView}), and read one ({@link ./ViewMarketView}).
 * They used to be a single component behind a single `:action/:uuid/:type` route, so its props were
 * the union of what all three needed — `type`, `isCustom`, `uuid`, `action`, `intentId`, every one
 * optional — and it re-derived its own mode from that bag on each render.
 *
 * Each job is a route and a component of its own now, and everything here takes *resolved* values: a
 * `MarketViewType`, not a `string` that might be one; a definite `isCustom`. Resolving those is each
 * page's job, and these signatures hold it to it.
 */
import {
	type CreateMarketViewRequest,
	type MarketScenario,
	type MarketViewAssetClassAliases,
	type MarketViewDetails,
	type MarketViewSettings,
	IntegrationMarketViewControllerApiFactory,
	MarketScenarioIds,
	MarketViewType,
} from "@eu.mdotm.api/sphere";
import { useFunctionalAreas } from "$root/App/context";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import { useApiGen } from "$root/api/hooks";
import { PageDownloadAndMoreActionsMenu } from "$root/components/PageDownloadAndMoreActionsMenu";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { removeDeltaFromSelectedMarketView } from "$root/components/Portfolio/MarketView/utilsV2";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { spawnCreateDialog } from "$root/components/spawnable/entity-management/create-dialog";
import { useEventBus, useGroupEventBus } from "$root/event-bus";
import { spawnAccessDialog } from "$root/functional-areas/acl/AccessDialog";
import { MarketViewPositioning } from "$root/functional-areas/market-view/MarketViewPositioning";
import { MarketViewProbabilities } from "$root/functional-areas/market-view/MarketViewProbabilities";
import { useMarketViewEntityManagementActions } from "$root/functional-areas/market-view/entity-management";
import { marketViewRepository } from "$root/functional-areas/market-view/repository";
import { platformToast } from "$root/notification-system/toast";
import { PortfolioStudioTab } from "$root/pages/PortfoliosStudio/portfolio-studio-tabs";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { useMappedQuery, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { downloadContentDisposition, objMatchFn, seconds } from "$root/utils";
import { Card } from "$root/widgets-architecture/layout/Card";
import { type Option, ProgressBar } from "@mdotm/shadcn/legacy/components";
import { Switch } from "@mdotm/shadcn/legacy/react-extensions";
import { noop, unpromisify } from "@mdotm/shadcn/legacy/utils";
import type { QueryObserverResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useCallback, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";

/** Where every page in the workspace goes when it's done, saved or cancelled. */
export const MARKET_VIEW_LIST_ROUTE = { tab: PortfolioStudioTab.MarketViews } as const;

/**
 * `LeavePrompt` matches on path prefix, and all three pages live under this one — so moving *within*
 * the workspace never counts as abandoning unsaved work.
 */
export const WORKSPACE_PATH_PREFIX = "/workspace_market";

/**
 * The user-level market view config (custom-view naming, commentary visibility) plus the asset class
 * aliases — everything the editor needs that isn't specific to one market view.
 *
 * The aliases' 404 / "Accesso negato" handling lives here because it's an access check on the feature
 * as a whole rather than on a single market view: without them, none of the three pages can draw.
 */
export function useMarketViewWorkspaceConfig(): {
	settings: MarketViewSettings | undefined;
	assetClassAliases: MarketViewAssetClassAliases | undefined;
} {
	const { t } = useTranslation();
	const { push } = useTypedNavigation();

	const querySettings = marketViewRepository.marketViewSettings.useQuery();
	const queryAssetClassAliases = marketViewRepository.assetClassAliases.useQuery();

	useQueryReactor(queryAssetClassAliases, {
		onError(error: AxiosError<{ code: number; message: string }>) {
			if (error.response?.data.code === 404 || error.response?.data.message === "Accesso negato") {
				spawnAccessDialog({
					onClick: (onClose) => {
						push("PortfoliosStudio", { tab: PortfolioStudioTab.References });
						onClose();
					},
				});
				return;
			}

			platformToast({
				children: t("SOMETHING_WENT_WRONG"),
				severity: "error",
				icon: "Portfolio",
			});
			push("PortfoliosStudio", { status: "notFound", tab: PortfolioStudioTab.References });
		},
	});

	return { settings: querySettings.data, assetClassAliases: queryAssetClassAliases.data };
}

/**
 * The market view being read or copied, kept live.
 *
 * This is what lets the route carry only a uuid: `marketViewType` and `custom` are properties of the
 * market view, so the two pages that already have one read them from here rather than from the URL.
 */
export function useMarketViewSummary(uuid: string): QueryObserverResult<MarketViewDetails, unknown> {
	const query = marketViewRepository.summary.useQuery(uuid);

	// Registered twice, as before the split: once so the area is claimed on mount, and again once the
	// name arrives, so the assistant's context picks it up.
	useFunctionalAreas(() => ({ areas: "marketView", data: { uuid } }), [uuid]);
	useFunctionalAreas(() => ({ areas: "marketView", data: { uuid, name: query.data?.name } }), [uuid, query.data?.name]);

	useEventBus("shared-entity", {
		filter: objMatchFn({ sharedEntityUuid: uuid }),
		listener: () => {
			query.refetch().catch(noop);
		},
	});

	// An import finishing is the one change to this market view that only the backend knows about,
	// and it has its own topic. `shared-entity` — the only subscription this page had — is fanned out
	// with skip-self, so it can never fire for a change the user (or a bot acting as them) made;
	// without this an import's result stayed hidden until the page was reloaded.
	useGroupEventBus("market-view-import-update", () => query.refetch().catch(noop), {
		filter: objMatchFn({ uuid }),
		timeout: seconds(2),
	});

	return query;
}

/** The scenario the editor is working on, plus the pristine copy that edits are compared against. */
export type ScenarioState = {
	scenariosQuery: QueryObserverResult<Option<string>[], unknown>;
	currentScenarioId: string | null;
	setCurrentScenarioId: (value: string | null) => void;
	marketScenario: MarketScenario | null;
	setMarketScenario: (value: MarketScenario | null) => void;
	originalScenario: MarketScenario | null;
	setOriginalScenario: (value: MarketScenario | null) => void;
};

/**
 * The selectable scenarios for a market view type, and the editor's working state.
 *
 * `type` is required because the scenario list is *per type* — there is no sensible request to make
 * without one. The two pages that read an existing market view get it from that view's summary, so
 * they must resolve the summary before calling this; taking the resolved value rather than doing the
 * waiting here is what keeps that ordering visible at the call site.
 */
export function useMarketViewScenarios(opts: {
	type: MarketViewType;
	isCustom: boolean;
	/** The market view being read or copied; absent when creating from scratch. */
	uuid?: string;
}): ScenarioState {
	const queryScenarioListRaw = marketViewRepository.scenarioList.useQuery({
		includeHistorical: false,
		investmentUuid: opts.uuid /* uuid: is referring to market view uuid , idk why is called investmentUuid*/,
		useEnhancement: false,
		marketViewType: opts.type,
		custom: opts.isCustom,
	});

	const scenariosQuery = useMappedQuery(
		(response) => response.map((x) => ({ label: x.label!, value: x.id! })),
		[],
		queryScenarioListRaw,
	);

	const [currentScenarioId, setCurrentScenarioId] = useState<string | null>(opts.uuid ?? null);
	const [originalScenario, setOriginalScenario] = useState<MarketScenario | null>(null);
	const [marketScenario, setMarketScenario] = useState<MarketScenario | null>(null);

	useQueryReactor(scenariosQuery, {
		onSuccess: (data) => {
			if (currentScenarioId === null && data.length > 0) {
				setCurrentScenarioId(data[0].value);
			}
		},
	});

	return {
		scenariosQuery,
		currentScenarioId,
		setCurrentScenarioId,
		marketScenario,
		setMarketScenario,
		originalScenario,
		setOriginalScenario,
	};
}

/**
 * Saving, which both creation flows share: cloning is still a `create` call, it just starts from
 * someone else's numbers. The read-only page never saves, which is precisely why this isn't part of
 * a shared page shell — it's opted into by the two pages that can.
 */
export function useSaveMarketView(opts: {
	type: MarketViewType;
	isCustom: boolean;
	scenario: ScenarioState;
	intentId?: string;
}): {
	/** Paths a successful save may navigate to without tripping the unsaved-changes prompt. */
	pathToNotBlock: string[];
	promptForNameAndSave: () => void;
} {
	const { type, isCustom, scenario, intentId } = opts;
	const { marketScenario, originalScenario, currentScenarioId } = scenario;
	const { t } = useTranslation();
	const { push } = useTypedNavigation();
	const [pathToNotBlock, setPathToNotBlock] = useState<string[]>([WORKSPACE_PATH_PREFIX]);

	const save = useCallback(
		async (name: string) => {
			await runWithErrorReporting(
				async () => {
					if (!marketScenario || !originalScenario) {
						throw Error("unable to save a market view of undefined", { cause: marketScenario });
					}

					const { flexibleExpectedReturnsVolatility } = removeDeltaFromSelectedMarketView(
						marketScenario.flexibleExpectedReturnsVolatility!.assetClasses!,
						originalScenario.flexibleExpectedReturnsVolatility!.assetClasses!,
						marketScenario.regimeUserProbability!,
					);
					const created = await marketViewRepository.create({
						name,
						forecastHorizon: marketScenario.forecastHorizon,
						defaultRegimeProbabilities: marketScenario.regimeProbabilities,
						userRegimeProbabilities: marketScenario.regimeUserProbability,
						positioningIndicators: marketScenario.positioningIndicators,
						// The delta against the pristine scenario is what the user actually changed, and it's
						// stripped for an expected-returns view only. A positioning view sends its forecasts
						// through untouched.
						flexibleExpectedReturnsVolatility:
							type === MarketViewType.ExpectedReturnsVolatility
								? { assetClasses: flexibleExpectedReturnsVolatility }
								: marketScenario.flexibleExpectedReturnsVolatility,
						marketViewType: type,
						custom: isCustom,
						scenarioId: currentScenarioId ?? undefined,
					} satisfies CreateMarketViewRequest);

					trackMixPanelEvent("marketView.entityActions.create", {
						intentId,
						marketView: { uuid: created.uuid!, name: created.name! },
					});
					// Synchronous so the prompt sees the exemption before the push below reaches it —
					// otherwise saving trips the unsaved-changes guard on its own way out.
					flushSync(() =>
						setPathToNotBlock((latestPath) => [
							...latestPath,
							typedUrlForRoute("PortfoliosStudio", MARKET_VIEW_LIST_ROUTE),
						]),
					);
					push("PortfoliosStudio", MARKET_VIEW_LIST_ROUTE);
				},
				{ area: "market-views-settings", attemptedOperation: { message: "unable to create market view" } },
			);
		},
		[currentScenarioId, intentId, isCustom, marketScenario, originalScenario, push, type],
	);

	const promptForNameAndSave = useCallback(() => {
		spawnCreateDialog({
			header: "Market View name",
			inputLabel: "Name",
			placeholder: "Insert market view name",
			confirmButton: t("BUTTON.SAVE"),
			cancelButton: t("CANCEL"),
			checkIfNameIsAvailable: marketViewRepository.isMarketViewNameAvailable,
			onSubmitAsync: save,
		}).catch(noop);
	}, [save, t]);

	return { pathToNotBlock, promptForNameAndSave };
}

/**
 * Export, delete, rename and duplicate for a saved market view.
 *
 * Shared by the two pages that have one: viewing it, and — as before the split — cloning it, where
 * these act on the *source*. That's a strange thing to offer mid-copy (deleting the source strands
 * the page you're on), but it's what the combined component did, so it's preserved here rather than
 * quietly dropped. Worth revisiting separately.
 */
export function MarketViewActionsMenu({ uuid, summary }: { uuid: string; summary: MarketViewDetails | undefined }) {
	const { push } = useTypedNavigation();
	const integrationsApi = useApiGen(IntegrationMarketViewControllerApiFactory);
	const entityManagementActions = useMarketViewEntityManagementActions(summary, {
		onDuplicate: () => push("PortfoliosStudio", MARKET_VIEW_LIST_ROUTE),
		onDelete: () => push("PortfoliosStudio", MARKET_VIEW_LIST_ROUTE),
	});

	return (
		<PageDownloadAndMoreActionsMenu
			area="market-views"
			downloadActions={[
				{
					icon: "xls",
					onClickAsync: async () => {
						const response = await integrationsApi.exportMarketView(uuid, { responseType: "blob" });
						await downloadContentDisposition(response);
					},
					label: "Market View template",
					"data-qualifier": "MarketView/DropdownMenu/DropdownItem(MarketViewTempalte)",
				},
			]}
			moreActions={[
				...(entityManagementActions.deleteAsync
					? [
							{
								icon: "Delete" as const,
								onClick: unpromisify(entityManagementActions.deleteAsync),
								label: "Delete",
								"data-qualifier": "MarketView/DropdownMenu/DropdownItem(Delete)",
							},
						]
					: []),
				...(entityManagementActions.renameAsync
					? [
							{
								icon: "Edit" as const,
								onClick: unpromisify(entityManagementActions.renameAsync),
								label: "Rename",
								"data-qualifier": "MarketView/DropdownMenu/DropdownItem(Rename)",
							},
						]
					: []),
				...(entityManagementActions.duplicateAsync
					? [
							{
								icon: "Content-Copy" as const,
								onClick: () => push("MarketViewWorkSpace/Clone", { uuid }),
								label: "Duplicate",
								"data-qualifier": "MarketView/DropdownMenu/DropdownItem(Duplicate)",
							},
						]
					: []),
			]}
		/>
	);
}

/**
 * The editor itself: which of the two market view widgets to show, and how.
 *
 * Every way it varies is an explicit prop rather than a mode it infers, so each page declares what it
 * wants and there's no `action === "new"` reasoning left buried in here.
 */
export function MarketViewEditor({
	type,
	isCustom,
	mode,
	scenario,
	settings,
	assetClassAliases,
	showWidgetHeader,
	lockScenarioSelection,
	hidePositioning,
	remapCommentaryToDefaults,
}: {
	type: MarketViewType;
	isCustom: boolean;
	/** Whether the widgets accept edits. */
	mode: "view" | "edit";
	scenario: ScenarioState;
	settings: MarketViewSettings | undefined;
	assetClassAliases: MarketViewAssetClassAliases | undefined;
	/** The widget's own header — only the from-scratch flow shows it. */
	showWidgetHeader: boolean;
	/** A copy inherits its source's scenario and may not be pointed at a different one. */
	lockScenarioSelection: boolean;
	hidePositioning: boolean;
	/**
	 * Overlay MDOTM's default commentary and positioning onto the loaded scenario. Only meaningful
	 * when creating from scratch, where the user starts from Sphere's forecast rather than from
	 * numbers someone already saved.
	 */
	remapCommentaryToDefaults: boolean;
}): React.JSX.Element {
	const queryDefaultMarketViewScenario = marketViewRepository.scenario.useQuery({
		scenarioId: MarketScenarioIds.SphereForecastPositioningOneMonth,
		investmentUuid: undefined,
		useEnhancement: false,
		custom: false,
	});

	return (
		<ReactQueryWrapperBase query={scenario.scenariosQuery}>
			{(scenarioOptions) => (
				<Card>
					<Switch
						case={type}
						match={{
							[MarketViewType.PositioningIndicators]: () =>
								queryDefaultMarketViewScenario.isLoading ? (
									<ProgressBar value="indeterminate" />
								) : (
									<MarketViewPositioning
										mode={mode}
										marketScenarioProvider={async (id) => {
											const defaultScenario = queryDefaultMarketViewScenario.data;
											let loadedScenario = await marketViewRepository.scenario.fetchQuery({
												scenarioId: id,
												investmentUuid: undefined,
												useEnhancement: false,
												custom: isCustom,
											});

											if (remapCommentaryToDefaults) {
												loadedScenario = {
													...loadedScenario,
													positioningIndicators: {
														...loadedScenario.positioningIndicators,
														positioningIndicators: isCustom
															? loadedScenario.positioningIndicators?.positioningIndicators
															: loadedScenario.positioningIndicators?.positioningIndicators?.map((indicator) => {
																	const mapped = {
																		...indicator,
																		mdotmCommentary:
																			defaultScenario?.positioningIndicators?.positioningIndicators?.find(
																				(x) => x.microAssetClass === indicator.microAssetClass,
																			)?.commentary,
																		mdotmPositioning:
																			defaultScenario?.positioningIndicators?.positioningIndicators?.find(
																				(x) => x.microAssetClass === indicator.microAssetClass,
																			)?.defaultPositioning,
																	};
																	if (id !== MarketScenarioIds.SphereForecastPositioningOneMonth) {
																		mapped.commentary = "";
																	}

																	if (
																		id !== MarketScenarioIds.UserForecastPositioningOneMonth &&
																		id !== MarketScenarioIds.SphereForecastPositioningOneMonth
																	) {
																		mapped.mdotmCommentary = undefined;
																		mapped.mdotmPositioning = undefined;
																	}

																	return mapped;
																}),
													},
												};
											}
											scenario.setOriginalScenario(loadedScenario);
											return loadedScenario;
										}}
										scenarioOptions={scenarioOptions}
										marketScenario={scenario.marketScenario}
										onMarketScenarioChange={scenario.setMarketScenario}
										disableScenarioSelection={lockScenarioSelection}
										selectedScenarioId={scenario.currentScenarioId}
										onSelectedScenarioChange={scenario.setCurrentScenarioId}
										canReadCommentary={settings?.commentaryEditorVisible}
										alias={assetClassAliases?.assetClassAliases}
										header={{ hide: !showWidgetHeader }}
										marketViewSettings={isCustom ? settings : undefined}
										hideOutlookToopltip={
											isCustom ||
											!(
												scenario.currentScenarioId === MarketScenarioIds.UserForecastPositioningOneMonth ||
												scenario.currentScenarioId === MarketScenarioIds.SphereForecastPositioningOneMonth
											)
										}
									/>
								),
							[MarketViewType.ExpectedReturnsVolatility]: () => (
								<MarketViewProbabilities
									mode={mode}
									marketScenarioProvider={async (id) => {
										const loadedScenario = await marketViewRepository.scenario.fetchQuery({
											scenarioId: id,
											investmentUuid: undefined,
											useEnhancement: false,
											custom: isCustom,
										});

										scenario.setOriginalScenario(loadedScenario);
										return loadedScenario;
									}}
									marketScenario={scenario.marketScenario}
									onMarketScenarioChange={scenario.setMarketScenario}
									scenarioOptions={scenarioOptions}
									disableScenarioSelection={lockScenarioSelection}
									selectedScenarioId={scenario.currentScenarioId}
									onSelectedScenarioChange={scenario.setCurrentScenarioId}
									canReadCommentary={settings?.commentaryEditorVisible}
									alias={assetClassAliases?.assetClassAliases}
									header={{ hide: !showWidgetHeader }}
									marketViewSettings={isCustom ? settings : undefined}
									hidePositioning={hidePositioning}
								/>
							),
						}}
					/>
				</Card>
			)}
		</ReactQueryWrapperBase>
	);
}
